// ========================================
// Hook לניהול פריטים מוחרגים (Supabase + localStorage fallback)
// ========================================
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const LOCAL_STORAGE_KEY = 'aeg_excluded_skus';

/**
 * Hook to manage excluded items with Supabase persistence.
 * Falls back to localStorage for non-authenticated users.
 * 
 * @param {Object} user - Current authenticated user (or null)
 */
export const useExcludedItems = (user) => {
    const [excludedSkus, setExcludedSkus] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Load excluded items from Supabase or localStorage
    const fetchExcludedItems = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            if (user) {
                // Authenticated: fetch from Supabase
                const { data, error: dbError } = await supabase
                    .from('user_excluded_items')
                    .select('sku')
                    .eq('user_id', user.id);

                if (dbError) throw dbError;

                const skus = data?.map(item => item.sku) || [];
                setExcludedSkus(skus);

                // Sync to localStorage as backup
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(skus));
            } else {
                // Not authenticated: use localStorage
                const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
                if (stored) {
                    setExcludedSkus(JSON.parse(stored));
                } else {
                    setExcludedSkus([]);
                }
            }
        } catch (err) {
            console.error('Error fetching excluded items:', err);
            setError(err.message);

            // Fallback to localStorage on error
            try {
                const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
                if (stored) setExcludedSkus(JSON.parse(stored));
            } catch {
                setExcludedSkus([]);
            }
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    // Toggle exclusion status for a SKU
    const toggleExclusion = useCallback(async (sku) => {
        const isCurrentlyExcluded = excludedSkus.includes(sku);

        // Optimistically update UI
        const newExclusions = isCurrentlyExcluded
            ? excludedSkus.filter(s => s !== sku)
            : [...excludedSkus, sku];

        setExcludedSkus(newExclusions);
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newExclusions));

        if (user) {
            try {
                if (isCurrentlyExcluded) {
                    // Remove from Supabase
                    const { error } = await supabase
                        .from('user_excluded_items')
                        .delete()
                        .eq('user_id', user.id)
                        .eq('sku', sku);

                    if (error) throw error;
                } else {
                    // Add to Supabase
                    const { error } = await supabase
                        .from('user_excluded_items')
                        .insert({ user_id: user.id, sku });

                    if (error) throw error;
                }
            } catch (err) {
                console.error('Error syncing exclusion to Supabase:', err);
                // Revert optimistic update on error
                setExcludedSkus(excludedSkus);
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(excludedSkus));
            }
        }
    }, [excludedSkus, user]);

    // Check if a SKU is excluded
    const isExcluded = useCallback((sku) => {
        return excludedSkus.includes(sku);
    }, [excludedSkus]);

    // Clear all exclusions
    const clearExclusions = useCallback(async () => {
        setExcludedSkus([]);
        localStorage.removeItem(LOCAL_STORAGE_KEY);

        if (user) {
            try {
                const { error } = await supabase
                    .from('user_excluded_items')
                    .delete()
                    .eq('user_id', user.id);

                if (error) throw error;
            } catch (err) {
                console.error('Error clearing exclusions in Supabase:', err);
            }
        }
    }, [user]);

    // Migrate localStorage data to Supabase when user logs in
    const migrateLocalStorageToSupabase = useCallback(async () => {
        if (!user) return;

        try {
            const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (!stored) return;

            const localSkus = JSON.parse(stored);
            if (!localSkus.length) return;

            // Get existing exclusions from Supabase
            const { data: existing } = await supabase
                .from('user_excluded_items')
                .select('sku')
                .eq('user_id', user.id);

            const existingSkus = new Set(existing?.map(item => item.sku) || []);

            // Find SKUs that need to be migrated
            const skusToMigrate = localSkus.filter(sku => !existingSkus.has(sku));

            if (skusToMigrate.length > 0) {
                const rows = skusToMigrate.map(sku => ({ user_id: user.id, sku }));
                const { error } = await supabase
                    .from('user_excluded_items')
                    .insert(rows);

                if (error) throw error;
                console.log(`Migrated ${skusToMigrate.length} exclusions to Supabase`);
            }

            // Refresh the list after migration
            await fetchExcludedItems();
        } catch (err) {
            console.error('Error migrating localStorage to Supabase:', err);
        }
    }, [user, fetchExcludedItems]);

    // Initial load and migration
    useEffect(() => {
        fetchExcludedItems();
    }, [fetchExcludedItems]);

    // Migrate on login
    useEffect(() => {
        if (user) {
            migrateLocalStorageToSupabase();
        }
    }, [user, migrateLocalStorageToSupabase]);

    return {
        excludedSkus,
        toggleExclusion,
        isExcluded,
        clearExclusions,
        isLoading,
        error,
        refetch: fetchExcludedItems
    };
};
